import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ReactFlow, Background, Controls, Node, Edge, useNodesState, useEdgesState } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ChevronLeftIcon, InfoIcon } from 'lucide-react';
import { getClusterById, getConstellationView, getNodeById, storyGalaxyData, StoryGalaxyNode } from '../../data/storyGalaxyData';
import { GenreNode } from './components/GenreNode';
import { GravityPanel } from './components/GravityPanel';
import { StoryStackBar } from './components/StoryStackBar';
import { useStoryStack } from '../../hooks/useStoryStack';
import { useDarkClasses } from '../../hooks/useDarkClasses';
const nodeTypes = {
  genre: GenreNode
};
const themeMap = {
  'violet-cosmic': {
    from: 'from-violet-900',
    to: 'to-purple-900'
  },
  'peach-gold': {
    from: 'from-amber-100',
    to: 'to-orange-200'
  },
  'electric-blue': {
    from: 'from-blue-900',
    to: 'to-cyan-800'
  }
};
const ClusterDetail = () => {
  const {
    clusterId,
    nodeId
  } = useParams<{
    clusterId?: string;
    nodeId?: string;
  }>();
  const navigate = useNavigate();
  const dk = useDarkClasses();
  const {
    selectedTags,
    addTag,
    removeTag,
    clearAll
  } = useStoryStack();
  const [selectedNode, setSelectedNode] = useState<StoryGalaxyNode | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const isNodeMode = !!nodeId;
  // Get the center node (either from the URL param or from the cluster's constellation view)
  const centerNode = useMemo(() => {
    if (isNodeMode && nodeId) return getNodeById(nodeId);
    if (clusterId) {
      const view = getConstellationView(clusterId);
      return view ? getNodeById(view.centerNodeId) : null;
    }
    return null;
  }, [isNodeMode, nodeId, clusterId]);
  // Get the cluster for visual theming
  const cluster = useMemo(() => {
    if (isNodeMode && centerNode) return getClusterById(centerNode.primaryClusterId);
    if (clusterId) return getClusterById(clusterId);
    return null;
  }, [isNodeMode, centerNode, clusterId]);
  // Auto-open panel for center node on load (desktop only — on mobile it blocks the map)
  useEffect(() => {
    if (centerNode) {
      setSelectedNode(centerNode);
      const isDesktop = window.innerWidth >= 768;
      if (isDesktop) {
        setIsPanelOpen(true);
      }
    }
  }, [centerNode?.id]);
  // Generate nodes and edges based on mode
  const {
    initialNodes,
    initialEdges
  } = useMemo(() => {
    if (!cluster || !centerNode) return {
      initialNodes: [],
      initialEdges: []
    };
    const nodes: Node[] = [];
    const edges: Edge[] = [];
    const centerX = window.innerWidth < 768 ? 200 : 400;
    const centerY = 300;
    const radius = window.innerWidth < 768 ? 150 : 250;
    // Determine visible nodes based on mode
    let visibleNodeIds: string[] = [];
    if (isNodeMode) {
      visibleNodeIds = [centerNode.id, ...centerNode.nearbyNodeIds];
    } else if (clusterId) {
      const view = getConstellationView(clusterId);
      if (view) visibleNodeIds = view.visibleNodeIds;
    }
    const visibleNodes = visibleNodeIds.map(getNodeById).filter(Boolean) as StoryGalaxyNode[];
    const orbitingNodes = visibleNodes.filter(n => n.id !== centerNode.id);
    // Add center node
    nodes.push({
      id: centerNode.id,
      type: 'genre',
      position: {
        x: centerX,
        y: centerY
      },
      data: {
        name: centerNode.name,
        type: centerNode.type,
        colorKey: centerNode.type === 'genre' ? centerNode.name : 'Romance',
        isDarkMode: dk.isDark
      }
    });
    // Add orbiting nodes radially
    orbitingNodes.forEach((node, index) => {
      const angle = index / orbitingNodes.length * 2 * Math.PI;
      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);
      let colorKey = 'default';
      if (node.type === 'genre') colorKey = node.name;
      if (node.type === 'romance_subgenre') colorKey = 'Romance';
      if (node.type === 'story_engine') colorKey = 'Science Fiction';
      nodes.push({
        id: node.id,
        type: 'genre',
        position: {
          x,
          y
        },
        data: {
          name: node.name,
          type: node.type,
          colorKey,
          isDarkMode: dk.isDark
        }
      });
    });
    // Add edges
    storyGalaxyData.gravityEdges.forEach(edge => {
      if (visibleNodeIds.includes(edge.sourceNodeId) && visibleNodeIds.includes(edge.targetNodeId)) {
        edges.push({
          id: edge.id,
          source: edge.sourceNodeId,
          target: edge.targetNodeId,
          type: 'straight',
          animated: true,
          style: {
            stroke: dk.isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.2)',
            strokeWidth: Math.max(1, edge.weight * 4),
            opacity: 0.3 + edge.weight * 0.5
          }
        });
      }
    });
    return {
      initialNodes: nodes,
      initialEdges: edges
    };
  }, [cluster, centerNode, isNodeMode, clusterId, dk.isDark]);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  // Update nodes/edges when initial data changes (e.g. navigation)
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    navigate(`/story-galaxy/node/${node.id}`);
  }, [navigate]);
  const handleToggleTag = (tag: string) => {
    if (selectedTags.includes(tag)) {
      removeTag(tag);
    } else {
      addTag(tag);
    }
  };
  const handleViewNode = (id: string) => {
    navigate(`/story-galaxy/node/${id}`);
    setIsPanelOpen(false); // Close panel briefly during transition
  };
  if (!cluster || !centerNode) {
    return <div className={`min-h-screen flex items-center justify-center ${dk.page}`} data-id="element-14008" data-mp="1qwovqhz88drs">
        <div className="text-center" data-id="element-14009" data-mp="1mwo6016ogg5f">
          <h2 className={`text-2xl font-bold mb-4 ${dk.textHeading}`} data-id="element-14010" data-mp="1mxpub7cpez53">
            Constellation Not Found
          </h2>
          <button onClick={() => navigate('/story-galaxy')} className="text-indigo-500 hover:underline" data-id="element-14011" data-mp="20p4evim9f8e9">
            Return to Story Galaxy
          </button>
        </div>
      </div>;
  }
  const theme = themeMap[cluster.visualTheme] || themeMap['violet-cosmic'];
  return <div className={`h-screen flex flex-col ${dk.page} overflow-hidden relative`} data-id="element-14012" data-mp="2jm5iaz0610g3">
      {/* Background Gradient matching cluster */}
      <div className={`absolute inset-0 bg-gradient-to-br ${theme.from} ${theme.to} ${dk.isDark ? 'opacity-20' : 'opacity-10'} pointer-events-none`} data-id="element-14013" data-mp="2eocgei5waslm" />

      {/* Header Overlay */}
      <div className={`absolute top-0 left-0 right-0 z-10 p-4 sm:p-6 bg-gradient-to-b ${dk.isDark ? 'from-gray-900' : 'from-white'} to-transparent pointer-events-none`} data-id="element-14014" data-mp="ka31xxmu8m6n">
        <div className="max-w-7xl mx-auto flex items-start justify-between pointer-events-auto" data-id="element-14015" data-mp="30vpz4orkdqgs">
          <div data-id="element-14016" data-mp="2n4uw3t3qy3l4">
            <Link to={isNodeMode ? `/story-galaxy/${centerNode.primaryClusterId}` : '/story-galaxy'} className={`inline-flex items-center text-sm font-medium ${dk.textSecondary} hover:${dk.textPrimary} transition-colors mb-2`} data-id="element-14017" data-mp="2n3a1y4lcgmns">
              <ChevronLeftIcon className="h-4 w-4 mr-1" data-id="element-14018" data-mp="2i2m2cuj1uxhy" />
              {isNodeMode ? `Back to ${cluster.name}` : 'Back to Galaxy'}
            </Link>
            <div className="flex items-center gap-2" data-id="element-14019" data-mp="287u4rqpgwan6">
              <h1 className={`text-2xl sm:text-3xl font-bold ${dk.textHeading} tracking-tight`} data-id="element-14020" data-mp="288lyevzi7yat">
                {isNodeMode ? centerNode.name : cluster.name}
              </h1>
              {selectedNode && !isPanelOpen && <button onClick={() => setIsPanelOpen(true)} className={`md:hidden p-1.5 rounded-lg transition-colors ${dk.isDark ? 'bg-white/10 hover:bg-white/20 text-white/70' : 'bg-black/5 hover:bg-black/10 text-gray-500'}`} aria-label="View node details" data-id="element-14021" data-mp="2qax93u1biuiz">
                  <InfoIcon className="h-5 w-5" data-id="element-14022" data-mp="2bkpz82pdmjtw" />
                </button>}
            </div>
            <p className={`text-sm ${dk.textSecondary} mt-1 max-w-md hidden sm:block`} data-id="element-14023" data-mp="22lvvlsdmot6u">
              {isNodeMode ? centerNode.shortDefinition : 'Click on any constellation node to explore gravity tags and build your Story Stack.'}
            </p>
          </div>
        </div>
      </div>

      {/* React Flow Canvas */}
      <div className="flex-1 w-full h-full relative" data-id="element-14024" data-mp="3rypxl98kq1f1">
        <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onNodeClick={onNodeClick} nodeTypes={nodeTypes} fitView fitViewOptions={{
        padding: 0.2
      }} minZoom={0.5} maxZoom={2} className="bg-transparent" proOptions={{
        hideAttribution: true
      }} data-id="element-14025" data-mp="3n1he95bovrc7">
          <Background color={dk.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'} gap={24} size={2} data-id="element-14026" data-mp="3cjvnjtl1fhdl" />
          <Controls className={`${dk.isDark ? 'bg-white/10 border-white/20 fill-white !text-white' : 'bg-black/5 border-black/10 fill-black !text-black'}`} showInteractive={false} data-id="element-14027" data-mp="3gm8sifrynb9b" />
        </ReactFlow>
      </div>

      {/* Gravity Side Panel */}
      <GravityPanel node={selectedNode} isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)} selectedStackTags={selectedTags} onToggleTag={handleToggleTag} onViewNode={handleViewNode} data-id="element-14028" data-mp="10v4i6edjkvav" />

      {/* Persistent Story Stack Bar */}
      <StoryStackBar selectedTags={selectedTags} onRemoveTag={removeTag} onClearAll={clearAll} onAddTag={addTag} data-id="element-14029" data-mp="2kji35kc0ojhs" />
    </div>;
};
export default ClusterDetail;